import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { clearLoginRedirect, getLoginRedirect } from "@/lib/loginRedirect";
import { digitsOnly, phoneInputProps } from "@/lib/numberInput";
import Navbar from "@/components/Navbar";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Analytics } from "@/lib/analyticsShim";

const RAILAXIS_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/assets/Logo/Railaxis_logo-wbg.png`;

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "vendor", label: "Vendor" },
  { value: "contractor", label: "Contractor" },
];

export default function Login() {
  const { login, requestOtp, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [redirect] = useState(() => getLoginRedirect(params));
  const [role, setRole] = useState("contractor");
  const [adminForm, setAdminForm] = useState({ email: "", password: "" });
  const [otpForm, setOtpForm] = useState({ company: "", phone: "", otp: "" });
  const [otpRequested, setOtpRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const submitAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(adminForm.email, adminForm.password);
      toast.success("Welcome back!");
      clearLoginRedirect();
      navigate(redirect === "/" ? "/admin-dashboard" : redirect);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const requestLoginOtp = async ({ resend = false } = {}) => {
    setSubmitting(true);
    setError("");
    try {
      const data = await requestOtp({
        role,
        company: otpForm.company,
        phone: otpForm.phone,
      });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setOtpRequested(true);
      if (resend) {
        setOtpForm((prev) => ({ ...prev, otp: "" }));
      }
      toast.success(resend ? "OTP resent." : "OTP sent.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    await requestLoginOtp();
  };

  const resendOtp = async () => {
    await requestLoginOtp({ resend: true });
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await loginWithOtp({
        role,
        company: otpForm.company,
        phone: otpForm.phone,
        otp: otpForm.otp,
      });
      toast.success("Login successful!");
      clearLoginRedirect();
      navigate(redirect);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-md mx-auto px-6 py-16 md:py-24" data-testid="login-page">
        <div className="flex items-center gap-2 justify-center mb-6">
          <img
            src={RAILAXIS_LOGO_SRC}
            alt="RailAxis logo"
            className="w-14 h-14 rounded-md object-contain border border-gray-100 bg-white"
          />
        </div>
        <h1 className="font-display font-bold text-3xl text-[#111827] text-center tracking-tight">Welcome back</h1>
        <p className="text-center text-[#4B5563] mt-2 text-sm">Sign in to continue.</p>

        <div className="mt-6">
          <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Login As</label>
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setError("");
              setOtpRequested(false);
              setDevOtp("");
              setOtpForm({ company: "", phone: "", otp: "" });
            }}
            className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {role === "admin" ? (
          <form onSubmit={submitAdmin} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Email</label>
              <input
                type="email"
                required
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Password</label>
              <input
                type="password"
                required
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                data-testid="login-password-input"
              />
            </div>

            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full rounded-md py-3 font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>
        ) : (
          <form onSubmit={otpRequested ? verifyOtp : sendOtp} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Company Name</label>
              <input
                required
                value={otpForm.company}
                onChange={(e) => setOtpForm({ ...otpForm, company: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Phone Number</label>
              <input
                required
                value={otpForm.phone}
                onChange={(e) => setOtpForm({ ...otpForm, phone: digitsOnly(e.target.value, 10) })}
                {...phoneInputProps}
                className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
              />
            </div>
            {otpRequested && (
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">OTP</label>
                <input
                  required
                  value={otpForm.otp}
                  onChange={(e) => setOtpForm({ ...otpForm, otp: digitsOnly(e.target.value, 6) })}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                />
                {devOtp && <p className="text-xs text-[#4B5563] mt-1">Dev OTP: {devOtp}</p>}
              </div>
            )}

            {error && <div className="text-sm text-red-600">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full rounded-md py-3 font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {otpRequested ? "Verify OTP" : "Send OTP"}
            </button>
            {otpRequested && (
              <button
                type="button"
                onClick={resendOtp}
                disabled={submitting}
                className="w-full rounded-md py-2.5 text-sm font-semibold text-[#111827] border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Resend OTP
              </button>
            )}
          </form>
        )}

        <p className="text-sm text-center text-[#4B5563] mt-6">
          New to RailAxis?{" "}
          <Link to="/register" className="text-[#C8A96A] font-semibold hover:underline" data-testid="to-register-link">
            Create an account
          </Link>
        </p>
      </section>
    </div>
  );
}
